import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useRef,
} from 'react';
import { BaseProps, HTMLElementWithAnchor, Popover2025 } from './Popover2025';
import { invariant } from '@ardoq/invariant';
import { StyledPopoverContainer } from './StyledPopoverContainer';

type ControlledPopoverChildProps = {
  ref?: React.RefObject<HTMLElementWithAnchor | null>;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: React.AriaAttributes['aria-expanded'];
  'aria-controls'?: React.AriaAttributes['aria-controls'];
};

type ControlledPopoverProps = Partial<BaseProps> & {
  isOpen: boolean;
  onClose: VoidFunction;
  onClickOutside?: VoidFunction;
  content: ReactElement | null | (() => ReactElement | null);

  /** Anchor element */
  children: ReactElement<ControlledPopoverChildProps>;

  /** Id used to wire `aria-controls` on the trigger and the popover element. */
  id: string;

  /** Forwarded to the popover container — useful for hover-driven open state. */
  onMouseEnterPopover?: (e: React.MouseEvent) => void;
  onMouseLeavePopover?: (e: React.MouseEvent) => void;
};

/**
 * A wrapper that renders a popover with the same styled frame as
 * WithUncontrolledPopover (card body + arrow), but leaves all open/close
 * state management to the consumer.
 */
export const ControlledPopover = ({
  isOpen,
  onClose,
  onClickOutside,
  content,
  children,
  placement = { side: 'bottom', overflow: 'center' },
  id,
  onMouseEnterPopover,
  onMouseLeavePopover,
  ...popoverProps
}: ControlledPopoverProps): ReactElement => {
  const locallyCreatedRef = useRef<HTMLElementWithAnchor | null>(null);

  const child = Children.only(children);

  invariant(
    isValidElement(child),
    'ControlledPopover expects a single React element as its child.'
  );

  const childProps = child.props;
  const anchorRef = childProps.ref ?? locallyCreatedRef;

  const triggerElement = cloneElement(child, {
    ref: anchorRef,
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    'aria-controls': id,
  });

  const popoverContent = isOpen
    ? typeof content === 'function'
      ? content()
      : content
    : null;

  return (
    <>
      {triggerElement}
      <Popover2025
        isOpen={isOpen && popoverContent !== null}
        onClose={onClose}
        onClickOutside={onClickOutside}
        id={id}
        anchorRef={anchorRef}
        placement={placement}
        {...popoverProps}
      >
        <StyledPopoverContainer
          isOpen={isOpen}
          preferredSide={placement.side}
          anchorId={id}
          anchorRef={anchorRef}
          onMouseEnter={onMouseEnterPopover}
          onMouseLeave={onMouseLeavePopover}
        >
          {popoverContent}
        </StyledPopoverContainer>
      </Popover2025>
    </>
  );
};
